import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import { useAuth } from "../../providers/AuthContext";
import { getScoFormSettings, getUsersByTenant } from "../../api/Users";
import { submitOffboarding } from "../../api/Offboarding";
import OffboardingForm from "../../components/offboarding/OffboardingForm";
import OffboardingHeader from "../../components/offboarding/OffboardingHeader";
import {
  DEFAULT_DELETION_DAYS,
  EMPTY_OFFBOARDING,
  buildOffboardingPayload,
  validateOffboarding,
} from "../../components/offboarding/offboardingFormData";

/** Used until the settings arrive, and if they cannot be fetched at all. */
const FALLBACK_SETTINGS = {
  supportsOffboarding: false,
  offboardingAutomationEnabled: false,
  offboardingDeletionDefaultDays: DEFAULT_DELETION_DAYS,
  offboardingHelpText: "",
};

/** Shown when the page cannot tell whether this customer may offboard at all. */
const LOAD_ERROR_MESSAGE = "Could not load the offboarding page";

/** Used when the API registered the case but could not say more about the failed schedule. */
const NOT_SCHEDULED_MESSAGE =
  "The offboarding could not be scheduled automatically. Diflex will handle it manually.";

/**
 * The message shown when the submit fails. A 403 and the API's validation texts are the
 * cases the orderer can act on: a 400 carries orderer-facing wording as a plain string
 * ("This user already has an offboarding in progress."), so it is shown as-is instead of
 * being hidden behind the generic failure message.
 *
 * @param {object} error - The rejected axios error
 * @returns {string} The message for the error toast
 */
const submitErrorMessage = (error) => {
  const { status, data } = error?.response || {};

  if (status === 403) return "You are not allowed to offboard users for this customer";
  if (status === 400 && typeof data === "string" && data.trim()) return data.trim();

  return "Failed to send the offboarding request";
};

/** Today as yyyy-MM-dd in the user's own timezone, not UTC. */
const todayIsoDate = () => {
  const now = new Date();
  const localMs = now.getTime() - now.getTimezoneOffset() * 60000;
  return new Date(localMs).toISOString().slice(0, 10);
};

/**
 * The hub's "Offboard user" page: registers that a user is leaving and what should
 * happen to the account, the computer and the mailbox.
 *
 * @returns {JSX.Element} The page
 */
const OffboardUserPage = () => {
  const { tenantPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [value, setValue] = useState(EMPTY_OFFBOARDING);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The ref is what actually blocks a second click: React batches state updates, so two
  // clicks in the same tick would both still see isSubmitting as false — and two accepted
  // requests mean two offboarding cases for the same person.
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      try {
        const accessToken = await getAccessToken();

        // Caught per call: a users list that cannot be fetched still leaves a usable page
        // with an empty picker, rather than hiding the offboarding form altogether.
        const [formSettings, tenantUsers] = await Promise.all([
          getScoFormSettings(accessToken).catch(() => null),
          getUsersByTenant(tenantPublicId, accessToken).catch(() => []),
        ]);

        if (cancelled) return;
        setSettings({ ...FALLBACK_SETTINGS, ...(formSettings || {}) });
        setUsers(tenantUsers || []);

        // Without the settings the page cannot tell "not allowed" from "not reachable",
        // so the orderer is told rather than left with a silent unavailable message.
        if (!formSettings) toast.error(LOAD_ERROR_MESSAGE);
      } catch {
        if (!cancelled) toast.error(LOAD_ERROR_MESSAGE);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    loadPage();

    return () => {
      cancelled = true;
    };
  }, [tenantPublicId]);

  const handleBackClick = () => navigate(-1);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    const problem = validateOffboarding(value, todayIsoDate());
    if (problem) {
      toast.error(problem);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      // Fetched per submit, not cached at page load: MSAL refreshes it silently, so a page
      // left open past the token's lifetime still sends with a valid token.
      const accessToken = await getAccessToken();
      const result = await submitOffboarding(
        buildOffboardingPayload(value, { source: "Hub", tenantPublicId }),
        accessToken
      );

      // Long-lived on purpose: the orderer has to be able to read and note the case
      // reference before the toast disappears.
      toast.success(
        `Offboarding sent to Diflex. Case reference: ${result.caseReference}`,
        {
          duration: 12000,
          iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" },
        }
      );

      // Registered but not scheduled: the dates are in the case, yet nothing will run by
      // itself, so the orderer must know Diflex has to pick it up manually.
      if (result.scheduled === false && settings.offboardingAutomationEnabled) {
        toast.error(result.schedulingMessage || NOT_SCHEDULED_MESSAGE);
      }
    } catch (error) {
      toast.error(submitErrorMessage(error));
      return;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }

    navigate(-1);
  };

  const introText = settings.offboardingAutomationEnabled
    ? "The account will be disabled on the end date and deleted on the deletion date."
    : "Diflex will perform the offboarding manually on the dates you choose.";

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
            <Breadcrumbs />
            {!loaded ? (
              <p className="text-sm text-gray-600">Loading...</p>
            ) : !settings.supportsOffboarding ? (
              <>
                <p className="text-black">
                  Offboarding is not available right now
                </p>
                <p className="text-sm text-gray-600">
                  Contact Diflex support to offboard a user.
                </p>
              </>
            ) : (
              <>
                <OffboardingHeader onBack={handleBackClick} />
                <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
                  <p className="text-sm text-gray-600">{introText}</p>
                  <OffboardingForm
                    users={users}
                    settings={settings}
                    value={value}
                    onChange={setValue}
                  />
                  <div className="flex justify-end gap-4 pt-4">
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleBackClick}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      Send offboarding
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default OffboardUserPage;
